// =============================================================================
// StatusDonutChart — donut SVG nativo para distribución de presupuestos.
// Sin librerías externas. Paleta coherente con BudgetStatusBadge.
// =============================================================================

import type { StatusDistribution } from '../../../types/dashboard';

interface StatusDonutChartProps {
  data: StatusDistribution;
  size?: number;
}

interface Segment {
  key: keyof StatusDistribution;
  label: string;
  value: number;
  color: string;
}

// Paleta coherente con el resto (BudgetStatusBadge):
// BORRADOR=slate, ENVIADO=primary, ACEPTADO=green, RECHAZADO=red
const PALETTE: Record<keyof StatusDistribution, { label: string; color: string }> = {
  BORRADOR:  { label: 'Borrador',  color: '#94a3b8' }, // slate-400
  ENVIADO:   { label: 'Enviado',   color: '#008DD0' }, // primary
  ACEPTADO:  { label: 'Aceptado',  color: '#22c55e' }, // green-500
  RECHAZADO: { label: 'Rechazado', color: '#ef4444' }, // red-500
};

export function StatusDonutChart({ data, size = 180 }: StatusDonutChartProps) {
  const total =
    data.BORRADOR + data.ENVIADO + data.ACEPTADO + data.RECHAZADO;

  const segments: Segment[] = (Object.keys(PALETTE) as (keyof StatusDistribution)[]).map((k) => ({
    key: k,
    label: PALETTE[k].label,
    value: data[k],
    color: PALETTE[k].color,
  }));

  const radius = size / 2;
  const innerRadius = radius * 0.62;
  const cx = radius;
  const cy = radius;

  // Si no hay datos, renderizamos un donut vacío gris claro
  if (total === 0) {
    return (
      <div className="flex flex-col items-center gap-3">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={radius - 2} fill="#f1f5f9" />
          <circle cx={cx} cy={cy} r={innerRadius} fill="white" />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-slate-400"
            style={{ fontSize: 13 }}
          >
            Sin datos
          </text>
        </svg>
      </div>
    );
  }

  // Construir arcos
  let currentAngle = -Math.PI / 2; // empezar arriba
  const paths = segments
    .filter((s) => s.value > 0)
    .map((s) => {
      const angle = (s.value / total) * Math.PI * 2;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;

      const x1 = cx + radius * Math.cos(startAngle);
      const y1 = cy + radius * Math.sin(startAngle);
      const x2 = cx + radius * Math.cos(endAngle);
      const y2 = cy + radius * Math.sin(endAngle);
      const x3 = cx + innerRadius * Math.cos(endAngle);
      const y3 = cy + innerRadius * Math.sin(endAngle);
      const x4 = cx + innerRadius * Math.cos(startAngle);
      const y4 = cy + innerRadius * Math.sin(startAngle);

      const largeArc = angle > Math.PI ? 1 : 0;

      const d = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}`,
        'Z',
      ].join(' ');

      currentAngle = endAngle;

      return { d, color: s.color, key: s.key, label: s.label, value: s.value };
    });

  return (
    <div className="flex items-center gap-5 flex-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
        {paths.map((p) => (
          <path key={p.key} d={p.d} fill={p.color} stroke="white" strokeWidth={2} />
        ))}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-slate-900"
          style={{ fontSize: 20, fontWeight: 700 }}
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-slate-500"
          style={{ fontSize: 11 }}
        >
          presupuestos
        </text>
      </svg>

      <ul className="space-y-1.5 text-sm">
        {segments.map((s) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <li key={s.key} className="flex items-center gap-2 text-slate-700">
              <span
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-slate-600 min-w-[80px]">{s.label}</span>
              <span className="font-semibold text-slate-900">{s.value}</span>
              <span className="text-xs text-slate-400">({pct}%)</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
